import { useState } from 'react';
import { AnimatedPieGraph } from '../components/AnimatedPieGraph';

export function AnimatedPieGraphPage() {
  const [data, setData] = useState<any[]>([]);
  const [inputText, setInputText] = useState('');
  const [shouldDraw, setShouldDraw] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(0.05);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [labelFontSize, setLabelFontSize] = useState(14);
  const [showLabels, setShowLabels] = useState(true);
  const [colorPalette, setColorPalette] = useState('default');
  const [captureVideo, setCaptureVideo] = useState(false);

  const colorOptions = [
    { value: 'default', label: 'Default' },
    { value: 'pastel', label: 'Pastel' },
    { value: 'vivid', label: 'Vivid' },
    { value: 'monochrome', label: 'Monochrome' }
  ];

  const parseData = (text: string) => {
    const lines = text.trim().split('\n');
    const parsedData: any[] = [];
    let headers: string[] = [];
    
    // Check if first row contains headers
    if (lines.length > 0) {
      const firstLine = lines[0];
      const firstValues = firstLine.split(/[\t,]+/).filter(val => val.trim() !== '');
      const hasHeaderRow = firstValues.length >= 2 && isNaN(parseFloat(firstValues[1]));
      
      if (hasHeaderRow) {
        headers = firstValues;
        lines.shift(); // Remove the header row
      } else {
        // Generate default headers if none provided
        const firstDataValues = firstLine.split(/[\t,]+/).filter(val => val.trim() !== '');
        headers = ['Timestamp', ...firstDataValues.slice(1).map((_, i) => `Category ${i + 1}`)];
      }
    }
    
    lines.forEach((line, index) => {
      const values = line.split(/[\t,]+/).filter(val => val.trim() !== '');
      if (values.length >= 2) {
        const timestamp = values[0];
        const dataValues = values.slice(1).map(val => parseFloat(val)).filter(val => !isNaN(val));
        
        if (dataValues.length > 0) {
          const total = dataValues.reduce((sum, val) => sum + val, 0);
          const percentages = dataValues.map(val => (val / total) * 100);
          
          const frameData = {
            timestamp,
            total,
            categories: headers.slice(1).map((header, i) => ({
              name: header,
              value: dataValues[i] || 0,
              percentage: percentages[i] || 0
            }))
          };
          
          parsedData.push(frameData);
        }
      }
    });
    
    return parsedData;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    setShouldDraw(false);
  };

  const handleDrawClick = () => {
    if (!inputText.trim()) {
      alert('Please enter some data first');
      return;
    }
    try {
      const parsedData = parseData(inputText);
      setData(parsedData);
      setShouldDraw(true);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Error parsing data');
    }
  };

  const handleClearClick = () => {
    setInputText('');
    setData([]);
    setShouldDraw(false);
  };

  const handleUseExampleData = async () => {
    // Example fallback data for pie chart with time series
    const fallbackText = `Year,Technology,Healthcare,Finance,Education\n2020,30,25,20,25\n2021,35,20,25,20\n2022,40,15,30,15\n2023,45,10,35,10\n2024,50,5,40,5`;
    setInputText(fallbackText);
  };

  const handleRecordingComplete = (blob: Blob) => {
    // Placeholder for video download logic
    setCaptureVideo(false);
  };

  return (
    <div style={{
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <h1 style={{
        color: '#343a40',
        marginBottom: '20px',
        fontSize: '24px'
      }}>
        Animated Pie Graph
      </h1>

      {/* Text entry box */}
      <div style={{
        marginBottom: '20px',
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <h2 style={{
          color: '#495057',
          marginBottom: '15px',
          fontSize: '18px'
        }}>
          Enter Data
        </h2>
        <textarea
          value={inputText}
          onChange={handleInputChange}
          placeholder="Enter data in the format:\nTimestamp,Category1,Category2,Category3\n2020,30,20,50\n2021,25,30,45\n2022,40,15,45"
          style={{
            width: '100%',
            height: '150px',
            padding: '12px',
            border: '1px solid #ced4da',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'monospace',
            resize: 'vertical',
            overflowX: 'auto',
            whiteSpace: 'nowrap'
          }}
        />
        <div style={{
          display: 'flex',
          gap: '10px',
          marginTop: '15px'
        }}>
          <button
            onClick={handleClearClick}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Clear
          </button>
          <button
            onClick={handleUseExampleData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Use Example Data
          </button>
        </div>
      </div>

      {/* Graph controls */}
      <div style={{
        marginBottom: '20px',
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <h2 style={{
          color: '#495057',
          marginBottom: '15px',
          fontSize: '18px'
        }}>
          Graph Controls
        </h2>
        <div className="mobile-controls-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: '15px'
        }}>
          <div className="mobile-control-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="animationSpeed" className="mobile-label" style={{ fontSize: '14px', color: '#495057' }}>
              Animation Speed
            </label>
            <input
              type="range"
              id="animationSpeed"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(parseFloat(e.target.value))}
              min="0.05"
              max="2.0"
              step="0.05"
              className="mobile-slider"
            />
            <span style={{ fontSize: '14px', color: '#495057' }}>{animationSpeed.toFixed(2)}</span>
          </div>

          <div className="mobile-control-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="backgroundColor" className="mobile-label" style={{ fontSize: '14px', color: '#495057' }}>
              Background
            </label>
            <select
              id="backgroundColor"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              className="mobile-select"
            >
              <option value="#ffffff">White</option>
              <option value="black">Black</option>
              <option value="#f0f0f0">Light Grey</option>
              <option value="#404040">Dark Grey</option>
            </select>
          </div>

          <div className="mobile-control-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="labelFontSize" className="mobile-label" style={{ fontSize: '14px', color: '#495057' }}>
              Label Size
            </label>
            <input
              type="range"
              id="labelFontSize"
              value={labelFontSize}
              onChange={(e) => setLabelFontSize(parseInt(e.target.value))}
              min="8"
              max="24"
              step="1"
              className="mobile-slider"
            />
            <span style={{ fontSize: '14px', color: '#495057' }}>{labelFontSize}px</span>
          </div>

          <div className="mobile-control-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="showLabels" className="mobile-label" style={{ fontSize: '14px', color: '#495057' }}>
              Show Labels
            </label>
            <input
              type="checkbox"
              id="showLabels"
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
              className="mobile-checkbox"
            />
          </div>

          <div className="mobile-control-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label htmlFor="colorPalette" className="mobile-label" style={{ fontSize: '14px', color: '#495057' }}>
              Color Palette
            </label>
            <select
              id="colorPalette"
              value={colorPalette}
              onChange={(e) => setColorPalette(e.target.value)}
              className="mobile-select"
            >
              {colorOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Draw Graph Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '20px',
        marginBottom: '20px'
      }}>
        <button
          onClick={handleDrawClick}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          Draw Graph
        </button>
        <button
          onClick={() => {
            setCaptureVideo(true);
            handleDrawClick();
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          Draw Graph and Capture Video
        </button>
      </div>

      {/* Graph display */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <h2 style={{
          color: '#495057',
          marginBottom: '15px',
          fontSize: '18px'
        }}>
          Graph Display
        </h2>
        <AnimatedPieGraph
          data={data}
          backgroundColor={backgroundColor}
          animationSpeed={animationSpeed}
          shouldDraw={shouldDraw}
          onRecordingComplete={handleRecordingComplete}
          labelFontSize={labelFontSize}
          showLabels={showLabels}
          colorPalette={colorPalette}
          captureVideo={captureVideo}
          showWatermark={true}
        />
      </div>
    </div>
  );
} 